import { useState, useContext } from 'react';
import PageContext from './PageContext';
import CCOffcanvas from './CCOffcanvas.tsx';
import OrderedDataTable from './OrderedDataTableCopy.jsx';
import ButtonComponent from './ButtonComponent.jsx';
import ApiRequestButton from './ApiRequestButton.jsx';
import { HttpContext } from '../../api-services/HttpService';
import { TableCell } from '@mui/material';

const tableHeader = [
    { columnname: 'mastercompanyname', columntag: 'Master Company Name', columnorder: 0 },
    { columnname: 'companyid',         columntag: 'Company ID',          columnorder: 1 },
    { columnname: 'field',             columntag: 'Field Changed',        columnorder: 2 },
    { columnname: 'oldvalue',          columntag: 'Old Value',            columnorder: 3 },
    { columnname: 'newvalue',          columntag: 'New Value',            columnorder: 4 },
    { columnname: 'status',            columntag: 'Status',               columnorder: 5 },
];

const parseValues = (valueStr) => {
    if (!valueStr) return {};
    let obj = {};
    valueStr.split(' | ').forEach(part => {
        let colonIdx = part.indexOf(':');
        if (colonIdx !== -1) {
            let key = part.slice(0, colonIdx).trim();
            let val = part.slice(colonIdx + 1).trim();
            obj[key] = val === 'null' ? null : val;
        }
    });
    return obj;
};

const getDisplayValue = (row, valueStr, fieldDisplayNameMap, payoutMethodMap, achAccountTypeMap) => {
    if (!valueStr || !row['field']) return valueStr ?? 'None';
    let parsed = parseValues(valueStr);
    
    // helper to get display value for a field
    const getFieldDisplayValue = (fieldKey, fieldValue) => {
        // if payoutmethodid, use payout method description instead of ID
        if (fieldKey === 'payoutmethodid' && payoutMethodMap && payoutMethodMap[fieldValue]) {
            return payoutMethodMap[fieldValue];
        }
        // if achaccounttype, use account type description instead of ID
        if (fieldKey === 'achaccounttype' && achAccountTypeMap && achAccountTypeMap[fieldValue]) {
            return achAccountTypeMap[fieldValue];
        }
        return fieldValue;
    };
    
    if (row['field'] === 'all') {
        // show all non-null fields for 'add' workflows
        let fields = Object.keys(parsed)
            .filter(k => parsed[k] !== null && parsed[k] !== undefined && parsed[k] !== 'null' && parsed[k] !== '')
            .map(k => {
                let displayName = fieldDisplayNameMap && fieldDisplayNameMap[k] ? fieldDisplayNameMap[k] : k;
                let displayValue = getFieldDisplayValue(k, parsed[k]);
                return `${displayName}: ${displayValue}`;
            });
        return fields.length > 0 ? fields.join(' | ') : 'None';
    }
    let changedFields = row['field'].split(', ').map(f => f.trim());
    if (changedFields.length === 1) {
        let val = parsed[changedFields[0]];
        if (val === null || val === undefined) return 'null';
        return getFieldDisplayValue(changedFields[0], val);
    }
    return changedFields.map(f => {
        let displayName = fieldDisplayNameMap && fieldDisplayNameMap[f] ? fieldDisplayNameMap[f] : f;
        let displayValue = getFieldDisplayValue(f, parsed[f] ?? 'null');
        return `${displayName}: ${displayValue}`;
    }).join(' | ');
};

const RowComponent = (row, count, header, stickyEnabled, fieldDisplayNameMap, payoutMethodMap, achAccountTypeMap) => {
    return (
        <>
            {header.map(el =>
                el['columnname'] === 'oldvalue' || el['columnname'] === 'newvalue' ?
                    <TableCell key={el['columnname']}>{getDisplayValue(row, row[el['columnname']], fieldDisplayNameMap, payoutMethodMap, achAccountTypeMap)}</TableCell>
                : el['columnname'] === 'field' ?
                    <TableCell key={el['columnname']}>
                        {!row[el['columnname']] ? 'None' : row[el['columnname']] === 'all' ? 'All' : row[el['columnname']].split(', ').map(f => fieldDisplayNameMap && fieldDisplayNameMap[f.trim()] ? fieldDisplayNameMap[f.trim()] : f.trim()).join(', ')}
                    </TableCell>
                : row[el['columnname']] == null ?
                    <TableCell key={el['columnname']}>None</TableCell>
                : <TableCell key={el['columnname']}>{row[el['columnname']]}</TableCell>
            )}
        </>
    );
};

const ClientProfileWorkflowActionOffcanvas = ({ checkList }) => {
    const {
        actionOffcanvas,
        setActionOffcanvas,
        actionType,
        refreshWorkflowData,
        snackbarAlert,
        fieldDisplayNameMap,
        payoutMethodMap,
        achAccountTypeMap,
    } = useContext(PageContext);

    const { PUT } = useContext(HttpContext);

    const [submissionComment, setSubmissionComment] = useState('');

    const onAction = () => {
        return new Promise((resolve, reject) => {
            if (!submissionComment || submissionComment.trim() === '') {
                snackbarAlert(true, 'Please enter a comment.', 'error');
                reject();
                return;
            }
            PUT('/client/clientProfileWorkflow', {
                workflowids: checkList.map(row => row['workflowid']),
                comment: submissionComment.trim(),
                action: actionType
            }).then(res => {
                console.log(res);
                setActionOffcanvas(false);
                setSubmissionComment('');
                snackbarAlert(true, 'Successfully updated workflow.', 'success');
                return refreshWorkflowData();
            }).then(res => {
                resolve(res);
            }).catch(err => {
                console.log(err);
                snackbarAlert(true, 'Error updating workflow.', 'error');
                reject(err);
            });
        });
    };

    const handleClose = () => {
        setSubmissionComment('');
        setActionOffcanvas(false);
    };

    return (
        <CCOffcanvas
            title={`${actionType} — ${checkList.length > 1 ? checkList.length + ' Companies' : '1 Company'}`}
            show={actionOffcanvas}
            onCanvasClose={handleClose}
            bodyCssClassName="offcanvas-body"
            offCanvasClassName="offcanvas-parent-card"
        >
            <div className="d-flex flex-column h-100">
                <div className="d-flex flex-column flex-grow-1 overflow-scroll">
                    {/* selected companies table */}
                    <div className="p-3 trail-section">
                        <div className="bg-white trail-card">
                            <OrderedDataTable
                                RowComponent={(row, count, header, stickyEnabled) => RowComponent(row, count, header, stickyEnabled, fieldDisplayNameMap, payoutMethodMap, achAccountTypeMap)}
                                tableHeader={tableHeader}
                                dataList={checkList}
                                numToPage={100}
                                disableMaxTableHeight={true}
                                stickyFirstColumnEnabled={false}
                            />
                        </div>
                    </div>
                    {/* comment input */}
                    <div className="p-3 comment-section">
                        <div className="bg-white comment-card">
                            <div className="p-3">
                                <div className="offcanvas-details pb-3" style={{ fontSize: '14px' }}>
                                    Comment
                                </div>
                                <input
                                    className="w-100 input-text-field"
                                    value={submissionComment}
                                    onChange={(e) => setSubmissionComment(e.target.value)}
                                    placeholder="Type comments here"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                {/* footer buttons */}
                <div className="d-flex justify-content-end p-3 w-100 offcanvas-footer z-2" style={{ height: '65px' }}>
                    <ButtonComponent
                        tag="Cancel"
                        border="#fcd800 1px solid"
                        background="#fff"
                        color="#001317"
                        onButtonClick={handleClose}
                    />
                    <ApiRequestButton onButtonClick={onAction} tag={actionType} />
                </div>
            </div>
        </CCOffcanvas>
    );
};

export default ClientProfileWorkflowActionOffcanvas;
